import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, TextField, Tooltip } from "@mui/material";
import { cloneDeep } from "lodash";

import { MenuSelect } from "components/Select/menuSelect";
import icon_snmp from "assets/images/icons/icn_Info_solid.svg";
import icon_snmp_hover from "assets/images/icons/icn_info_solid_hover.svg";


const LAYOUT_CONFIGS = {
  container: {
    width: "500px",
    ml: "20px",
    mt: "-10px",
  },
  labelWidth: "105px",
  selectWidth: "auto",
}

const MacWhitelistSection = ({
  macProfile = null,
  onChangeMacProfile,
  macProfileOptions = [],
  macProfilesRaw = [],
  showEditGeneral,
  showDynamicSize = false,
  dynamicSize = null,
  onChangeDynamicSize,
}) => {

  const theme = useTheme();
  const config = cloneDeep(LAYOUT_CONFIGS);

  const selectedOption = useMemo(() => {
    if (!macProfile) return null;
    return macProfileOptions.find((opt) => opt.value === macProfile || opt.label === macProfile);
  }, [macProfile, macProfileOptions]);

  const macCount = selectedOption?.macCount || 0;
  const isOverLimit = macCount > 32;

  return (
    <Box sx={{ width: config.container.width, boxSizing: 'border-box', ml: config.container.ml, mt: config.container.mt }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: "20px", mb: 1.5 }}>
        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[1],
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
            minWidth: "auto !important",
            mr: 0.5,
          }}
        >
          Allowed Devices
        </Typography>
        <Tooltip
          arrow
          placement="bottom"
          title={
            <Typography variant="body2" fontSize={13}>
              Add existing MAC Address Profiles. A maximum of 32 entries can be configured.
            </Typography>
          }
          componentsProps={{
            tooltip: {
              sx: { maxWidth: "350px" }
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: 16,
              "& img": {
                content: `url(${icon_snmp})`,
              },
              "&:hover img": {
                content: `url(${icon_snmp_hover})`,
              },
            }}
          >
            <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, mt: "20px" }}>
        <Typography
          sx={{
            width: config.labelWidth,
            color: theme.palette.primary.greyPrimary,
            fontSize: theme.typography.customFontSize[2],
            fontWeight: theme.typography.customFontWeight[0],
            whiteSpace: "nowrap",
          }}
        >
          MAC Address
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: config.selectWidth, flexGrow: 1 }}>
          <MenuSelect
            width="100%"
            value={macProfile || ""}
            onChange={(opt) => onChangeMacProfile(opt)}
            options={macProfileOptions}
            placeholder="Select a MAC Address Profile"
            pinningOptions={[
              {
                value: "Edit/Create New Profile",
                label: "Edit/Create New Profile",
                showArrow: true,
              }
            ]}
            disabled={!showEditGeneral}
          />
          {isOverLimit && (
            <Typography sx={{ color: theme.palette.primary.orange || 'orange', fontSize: theme.typography.customFontSize[1] || 12, mt: 0.5 }}>
              Selected Profile exceeds the supported entry limit.
            </Typography>
          )}
        </Box>
      </Box>

      {showDynamicSize && (
        <Box sx={{ mt: '20px' }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
            <Typography
              sx={{
                width: "150px",
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[0],
                whiteSpace: "nowrap",
              }}
            >
              Dynamic Whitelist Size Limit
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              type="number"
              value={dynamicSize ?? ""}
              sx={{
                width: "300px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: theme.shape.customBorderRadius[1],
                  height: "31px",
                  backgroundColor: theme.palette.primary.transparent,
                }
              }}
              onChange={(e) => onChangeDynamicSize(e.target.value)}
              disabled={!showEditGeneral}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", ml: "5px" }}>(1-32)</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(MacWhitelistSection);
